import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { StoreInfoPatch } from "../api/storesApi";
import { getStoresApi, getStoresPaginatedApi, createStoreApi, getStoreApi, getResellerStoresPaginatedApi, createResellerStoreApi, getChainStoresPaginatedApi, createChainStoreApi, updateStoreSettingsApi, getAdminStoreSearchKeyApi, getResellerStoreSearchKeyApi } from "../api/storesApi";
import type { StoreId, ResellerId, ChainId } from "@KassaroAB/shared/types/ids";
import type { DBUser } from "@KassaroAB/shared/types/user";
import { UserRole } from "@KassaroAB/shared/types/enums/UserRole";

export const useStoreSearchKeyQuery = (
  user: DBUser | undefined,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ["store", "search-key", user?.id, user?.role, user?.resellerId],
    queryFn: () => {
      if (user?.role === UserRole.RESELLER_ADMIN && user?.resellerId) {
        return getResellerStoreSearchKeyApi(user.resellerId);
      }
      return getAdminStoreSearchKeyApi();
    },
    enabled: enabled && !!user,
    retry: false,
  });
};

export const useGetStoresQuery = () => {
  return useQuery({
    queryKey: ["stores"],
    queryFn: () => getStoresApi(),
  });
};

export const useGetStoresInfiniteQuery = () => {
  return useInfiniteQuery({
    queryKey: ["stores", "admin"],
    queryFn: ({ pageParam }: { pageParam: string | null }) => getStoresPaginatedApi(pageParam, 20),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined;
      return lastPage.length > 0 ? lastPage[lastPage.length - 1].id : undefined;
    },
  });
};

export const useCreateStoreMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (store: {
      name: string;
      displayName: string;
      street: string;
      postalcode: string;
      city: string;
      country: string;
      currency: string;
      language: string;
      timeZone?: string;
      orgNumber: string;
      contactPerson: string;
      contactPhone: string;
      contactEmail: string;
      active?: boolean;
    }) => createStoreApi(store),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
    },
  });
};

export const useGetStoreQuery = (storeId: StoreId | undefined) => {
  return useQuery({
    queryKey: ["stores", storeId],
    queryFn: () => getStoreApi(storeId!),
    enabled: !!storeId,
  });
};

export const useUpdateStoreMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      storeId,
      store,
    }: {
      storeId: StoreId;
      store: StoreInfoPatch;
    }) => updateStoreSettingsApi(storeId, store),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["stores", variables.storeId] });
      queryClient.invalidateQueries({ queryKey: ["stores"] });
    },
  });
};

export const useGetResellerStoresInfiniteQuery = (resellerId: ResellerId | undefined) => {
  return useInfiniteQuery({
    queryKey: ["stores", "reseller", resellerId],
    queryFn: ({ pageParam }: { pageParam: string | null }) => getResellerStoresPaginatedApi(resellerId!, pageParam, 20),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined;
      return lastPage.length > 0 ? lastPage[lastPage.length - 1].id : undefined;
    },
    enabled: !!resellerId,
  });
};

export const useCreateResellerStoreMutation = (resellerId: ResellerId | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (store: {
      name: string;
      displayName: string;
      street: string;
      postalcode: string;
      city: string;
      country: string;
      currency: string;
      language: string;
      timeZone?: string;
      orgNumber: string;
      contactPerson: string;
      contactPhone: string;
      contactEmail: string;
      active?: boolean;
    }) => createResellerStoreApi(resellerId!, store),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
    },
  });
};

export const useGetChainStoresQuery = (
  chainId: ChainId | undefined,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["stores", "chain", chainId, "list"],
    queryFn: () => getChainStoresPaginatedApi(chainId!, null),
    enabled: enabled && !!chainId,
  });
};

export const useGetChainStoresInfiniteQuery = (chainId: ChainId | undefined) => {
  return useInfiniteQuery({
    queryKey: ["stores", "chain", chainId],
    queryFn: ({ pageParam }: { pageParam: string | null }) => getChainStoresPaginatedApi(chainId!, pageParam, 20),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined;
      return lastPage.length > 0 ? lastPage[lastPage.length - 1].id : undefined;
    },
    enabled: !!chainId,
  });
};

export const useCreateChainStoreMutation = (chainId: ChainId | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (store: {
      name: string;
      displayName: string;
      street: string;
      postalcode: string;
      city: string;
      country: string;
      currency: string;
      language: string;
      timeZone?: string;
      orgNumber: string;
      contactPerson: string;
      contactPhone: string;
      contactEmail: string;
      active?: boolean;
    }) => createChainStoreApi(chainId!, store),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
    },
  });
};